import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EmailSendCategory } from '@erp/shared-types';
import { T, useGT } from 'gt-react';
import {
  PaperPlaneRightIcon,
  XIcon,
  MinusIcon,
  ArrowsOutSimpleIcon,
  ArrowsInSimpleIcon,
  DownloadSimpleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { HtmlWYSIWYGEditor } from '@/components/common/HtmlWYSIWYGEditor';
import { ExcelDocViewer } from '@/components/xlsx-viewer/ExcelDocViewer';
import useOrganizationMembers from '@/api/organization/useOrganizationMembers';
import { useOrganizationContacts } from '@/api/rpc/hooks/communications/useOrganizationContacts';
import useOrganizationConfig from '@/api/core/organization-config/useOrganizationConfig';
import {
  useSendComposeReply,
  useComposeReplySendableMailboxes,
} from '@/api/rpc/hooks/communications/useSendComposeReply';
import {
  EMAIL_REGEX,
  makeRecipientId,
} from '@/components/common/email-composer/types';
import { ComposeRecipientField } from './ComposeRecipientField';
import { SUGGESTED_RESPONSES, type ComposeContact } from './composeReplyData';
import { ComposeAttachmentChips } from './ComposeAttachmentChips';
import {
  attachmentBudget,
  attachmentTotalBytes,
  composeReplySendErrorMessage,
  removeFileAt,
  removeRef,
  type AttachmentDocumentRef,
  type ComposeReplyAttachmentInput,
} from './composeReplyAttachments';

export type { AttachmentDocumentRef, ComposeReplyAttachmentInput };

/**
 * Stacking layer for the panel. `page` (default) is the ordinary `z-[60]`
 * floating window: dialogs the page opens (including this panel's own Excel
 * preview, at the 1400 dialog layer) sit above it. `above-dialog` portals the
 * panel to `document.body` at 1450 — above a host that is itself a modal
 * Dialog (1400) and below the 1500 popper layer the From select and
 * recipient suggestions render in. Only hosts rendering inside a Dialog
 * should ask for it, and only with document-ref attachments: in this mode
 * the panel's own Excel preview dialog (1400) would open underneath it.
 * Inline number rather than a `z-[…]` class, see
 * `packages/ui/src/lib/layer-style.ts`.
 */
export type ComposeReplyPanelLayer = 'page' | 'above-dialog';
const ABOVE_DIALOG_Z_INDEX = 1450;

const XLSX_CONTENT_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

function isXlsxAttachment(attachment: ComposeReplyAttachmentInput): boolean {
  return (
    XLSX_CONTENT_TYPES.has(attachment.contentType) ||
    /\.xlsx?$/i.test(attachment.name)
  );
}

/**
 * Rendered preview of an Excel attachment, opened by clicking its chip. The
 * base64 payload becomes a short-lived object URL for the local parser; when
 * the attachment also exists as a stored document, ExcelDocViewer offers the
 * Microsoft Office Online rendering (the real spreadsheet) as well.
 */
function AttachmentPreviewDialog({
  attachment,
  onClose,
}: {
  attachment: ComposeReplyAttachmentInput;
  onClose: () => void;
}) {
  const blobUrl = useMemo(() => {
    const byteString = atob(attachment.contentBytes);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      bytes[i] = byteString.charCodeAt(i);
    }
    return URL.createObjectURL(
      new Blob([bytes], { type: attachment.contentType }),
    );
  }, [attachment]);

  // Object URLs hold the blob in memory until revoked — release on close.
  useEffect(() => () => URL.revokeObjectURL(blobUrl), [blobUrl]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="truncate">{attachment.name}</DialogTitle>
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <a href={blobUrl} download={attachment.name}>
                <DownloadSimpleIcon className="mr-1 h-4 w-4" />
                <T>Download</T>
              </a>
            </Button>
          </div>
        </DialogHeader>
        <ExcelDocViewer
          url={blobUrl}
          fileName={attachment.name}
          documentId={attachment.documentId}
          height={520}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Parse a raw sender string ("Name <email>" or a bare address) into a contact.
 * Returns null when no valid email can be extracted.
 */
function parseEmailAddress(
  raw: string | null | undefined,
): ComposeContact | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  const angle = trimmed.match(/^(.*)<([^>]+)>\s*$/);
  const email = (angle ? angle[2] : trimmed).trim();
  if (!EMAIL_REGEX.test(email)) return null;
  const name = angle ? angle[1].trim().replace(/^"|"$/g, '') : '';
  return { id: makeRecipientId(), name: name || email, email };
}

interface ComposeReplyPanelProps {
  onClose: () => void;
  /** Sales-order draft the reply is composed from (threads + sender resolution). */
  salesOrderDraftId?: string;
  /** Prefilled subject line (e.g. "Re: Order PO-123"). */
  defaultSubject?: string;
  /** Raw sender of the originating email; seeded as the To recipient. */
  defaultToAddress?: string | null;
  /**
   * Addresses seeded into Cc alongside the org's configured defaults (e.g.
   * the approver cc'ing themselves on a reply to a requester). Deduped
   * against the defaults; still editable before sending.
   */
  defaultCcAddresses?: string[];
  /** Prefilled body HTML, loaded into the editor on open. */
  defaultBodyHtml?: string;
  /** Header title. Defaults to "Compose Email". */
  title?: string;
  /** Toast text shown after a successful send. Defaults to "Email sent!". */
  successMessage?: string;
  /** Whether to show the quick "Suggested responses" buttons. Defaults to true. */
  showSuggestedResponses?: boolean;
  /**
   * Distance in px to lift the panel off the bottom of the viewport. Pass a
   * page's fixed action-bar height so the panel (including its minimized
   * header) sits above the buttons instead of covering them. Defaults to 0.
   */
  bottomOffset?: number;
  /**
   * Files sent with the email (e.g. a generated approved-discounts workbook).
   * Seeds the panel's attachment list; the user can remove any of them
   * before sending (remount via `key` to reseed).
   */
  attachments?: ComposeReplyAttachmentInput[];
  /**
   * Which org-level default sender / default Cc list seeds the panel:
   * "orders" (sales-order contexts), "discounts" (discount-group contexts),
   * or "general" (everything else). Strict — no cross-category fallback.
   * Defaults to "general".
   */
  category?: EmailSendCategory;
  /**
   * Stored documents to attach by reference (forwarding an inbound email's
   * attachments) — resolved server-side, no base64 in the browser. Requires
   * `sourceInboundEmailId`. Seeds the list like `attachments`; removable.
   */
  attachmentDocumentRefs?: AttachmentDocumentRef[];
  /** The inbound email being forwarded (scopes the document attachments). */
  sourceInboundEmailId?: string;
  /** Where the panel stacks; see `ComposeReplyPanelLayer`. Defaults to `page`. */
  layer?: ComposeReplyPanelLayer;
}

/**
 * Gmail-style email composition mini-window anchored to the bottom-right of the
 * page. Non-modal: the page behind it stays interactive. Recipient suggestions
 * come from the org's members + email address book; CC is pre-seeded from the
 * org's configured default recipients. Send dispatches through the org's
 * Microsoft Graph (Azure) mailbox, threading the reply off the source email.
 */
export function ComposeReplyPanel({
  onClose,
  salesOrderDraftId,
  defaultSubject = '',
  defaultToAddress,
  defaultCcAddresses,
  defaultBodyHtml = '',
  title,
  successMessage,
  showSuggestedResponses = true,
  bottomOffset = 0,
  attachments,
  category = 'general',
  attachmentDocumentRefs,
  sourceInboundEmailId,
  layer = 'page',
}: ComposeReplyPanelProps) {
  const gt = useGT();
  const sendReply = useSendComposeReply();
  // The attachments that will actually be sent — seeded from the props, then
  // trimmed by the user. Graph's inline path caps the total, so the running
  // budget below gates Send instead of letting the server reject the email.
  const [files, setFiles] = useState<ComposeReplyAttachmentInput[]>(
    () => attachments ?? [],
  );
  const [refs, setRefs] = useState<AttachmentDocumentRef[]>(
    () => attachmentDocumentRefs ?? [],
  );
  const budget = attachmentBudget(attachmentTotalBytes(files, refs));
  // Seed To with the originating email's sender (if any).
  const [to, setTo] = useState<ComposeContact[]>(() => {
    const sender = parseEmailAddress(defaultToAddress);
    return sender ? [sender] : [];
  });
  const [cc, setCc] = useState<ComposeContact[]>([]);
  const [bcc, setBcc] = useState<ComposeContact[]>([]);
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyHtml, setBodyHtml] = useState(defaultBodyHtml);
  // Bumping the key remounts the editor so a chosen suggested response is read
  // in via `initialHtml` (the editor only consumes it on mount).
  const [editorKey, setEditorKey] = useState(0);
  const [minimized, setMinimized] = useState(false);
  // Attachment whose rendered preview dialog is open (chip click).
  const [previewAttachment, setPreviewAttachment] =
    useState<ComposeReplyAttachmentInput | null>(null);
  // Expanded = the panel takes over the whole screen.
  const [expanded, setExpanded] = useState(false);
  // The selected send-from mailbox UPN (required before sending).
  const [fromUpn, setFromUpn] = useState<string | undefined>(undefined);

  const { data: membersData } = useOrganizationMembers(true);
  const { contacts: orgContacts, isLoading: contactsLoading } =
    useOrganizationContacts();
  const { data: orgConfigData } = useOrganizationConfig();
  const { data: sendable } = useComposeReplySendableMailboxes(category);

  // Dropdown suggestions: active org members + the org address book (internal
  // contacts), deduped by address. Free-typed external emails are still
  // accepted by the field.
  const contacts = useMemo<ComposeContact[]>(() => {
    const byEmail = new Map<string, ComposeContact>();
    const add = (contact: ComposeContact) => {
      const key = contact.email.trim().toLowerCase();
      if (!key || byEmail.has(key)) return;
      byEmail.set(key, contact);
    };
    for (const member of membersData?.members ?? []) {
      if (member.deactivatedAt === null && member.email) {
        add({ id: member.userId, name: member.name, email: member.email });
      }
    }
    for (const entry of orgContacts) {
      if (entry.email) {
        add({ id: entry.id, name: entry.name, email: entry.email });
      }
    }
    return Array.from(byEmail.values());
  }, [membersData, orgContacts]);

  // Seed CC once from the org's configured default recipients, resolving each
  // id against the address book. Adjust-state-during-render guard (no effect),
  // matching the pattern in EmailDraftSheet.
  const [seeded, setSeeded] = useState(false);
  if (
    !seeded &&
    membersData !== undefined &&
    !contactsLoading &&
    orgConfigData !== undefined
  ) {
    setSeeded(true);
    const orgConfig = orgConfigData.organizationConfig;
    const ids =
      (category === 'orders'
        ? orgConfig?.defaultCcRecipientIdsOrders
        : category === 'purchase_orders'
          ? orgConfig?.defaultCcRecipientIdsPurchaseOrders
          : category === 'discounts'
            ? orgConfig?.defaultCcRecipientIdsDiscounts
            : orgConfig?.defaultCcRecipientIds) ?? [];
    // Stored ids are address-book contact ids OR member user ids (the
    // settings picker offers both sources).
    const byId = new Map(orgContacts.map((c) => [c.id, c]));
    const memberById = new Map(
      (membersData?.members ?? [])
        .filter((m) => m.deactivatedAt === null && !!m.email)
        .map((m) => [m.userId, m]),
    );
    const defaults: ComposeContact[] = [];
    for (const id of ids) {
      const entry = byId.get(id);
      if (entry?.email) {
        defaults.push({ id: entry.id, name: entry.name, email: entry.email });
        continue;
      }
      const member = memberById.get(id);
      if (member) {
        defaults.push({
          id: member.userId,
          name: member.name,
          email: member.email,
        });
      }
    }
    for (const raw of defaultCcAddresses ?? []) {
      const contact = parseEmailAddress(raw);
      if (
        contact &&
        !defaults.some(
          (c) => c.email.toLowerCase() === contact.email.toLowerCase(),
        )
      ) {
        defaults.push(contact);
      }
    }
    if (defaults.length > 0) setCc(defaults);
  }

  // Seed the From mailbox once from the org's default send mailbox (if any).
  const [fromSeeded, setFromSeeded] = useState(false);
  if (!fromSeeded && sendable !== undefined) {
    setFromSeeded(true);
    if (sendable.defaultUpn) setFromUpn(sendable.defaultUpn);
  }

  function insertSuggestedResponse(html: string) {
    setBodyHtml(html);
    setEditorKey((k) => k + 1);
  }

  async function handleSend() {
    if (!fromUpn) return;
    try {
      await sendReply.mutateAsync({
        salesOrderDraftId,
        to: to.map((r) => r.email),
        cc: cc.length > 0 ? cc.map((r) => r.email) : undefined,
        bcc: bcc.length > 0 ? bcc.map((r) => r.email) : undefined,
        subject,
        htmlContent: bodyHtml,
        mailboxUpn: fromUpn,
        attachments: files.length ? files : undefined,
        attachmentDocumentIds: refs.length
          ? refs.map((ref) => ref.documentId)
          : undefined,
        sourceInboundEmailId,
      });
      toast.success(
        successMessage ??
          gt('Email sent!', { context: 'compose panel send success toast' }),
      );
      onClose();
    } catch (error) {
      toast.error(
        composeReplySendErrorMessage(
          error,
          gt('Failed to send reply', {
            context: 'compose reply send error toast',
          }),
        ),
      );
    }
  }

  const panel = (
    <div
      className={cn(
        'fixed',
        layer === 'page' && 'z-[60]',
        expanded
          ? 'inset-0 w-full'
          : 'bottom-0 w-[480px] max-w-[calc(100vw-2rem-var(--agent-panel-width,0px))]',
      )}
      // Docked mode shifts left by the Nami panel's width so the two never overlap.
      style={{
        ...(expanded
          ? {}
          : {
              bottom: bottomOffset,
              right: 'calc(1rem + var(--agent-panel-width, 0px))',
            }),
        ...(layer === 'above-dialog' ? { zIndex: ABOVE_DIALOG_Z_INDEX } : {}),
      }}
    >
      <div
        className={cn(
          'flex animate-in flex-col overflow-hidden border bg-background shadow-2xl duration-200 fade-in slide-in-from-bottom-4',
          expanded ? 'h-full rounded-none' : 'max-h-[85vh] rounded-t-lg',
        )}
      >
        {/* Header bar */}
        <div className="flex shrink-0 items-center justify-between bg-primary px-4 py-2 text-primary-foreground">
          <span className="truncate text-sm font-medium">
            {title ?? <T context="compose reply panel title">Compose Email</T>}
          </span>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setMinimized((m) => !m)}
              aria-label={gt('Minimize', {
                context: 'compose reply minimize button',
              })}
              className="inline-flex size-6 items-center justify-center rounded hover:bg-white/20"
            >
              <MinusIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              aria-label={
                expanded
                  ? gt('Exit full screen', {
                      context: 'compose reply collapse button',
                    })
                  : gt('Full screen', {
                      context: 'compose reply expand button',
                    })
              }
              className="inline-flex size-6 items-center justify-center rounded hover:bg-white/20"
            >
              {expanded ? (
                <ArrowsInSimpleIcon className="h-4 w-4" />
              ) : (
                <ArrowsOutSimpleIcon className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={gt('Close', {
                context: 'compose reply close button',
              })}
              className="inline-flex size-6 items-center justify-center rounded hover:bg-white/20"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!minimized && (
          <>
            {/* Headers + body (scrollable) */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div className="divide-y divide-border border-b">
                {/* From — choose the sending mailbox */}
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="w-9 shrink-0 text-xs font-medium text-muted-foreground">
                    <T context="email field label From">From</T>
                  </span>
                  {sendable && sendable.mailboxes.length === 0 ? (
                    <span className="text-sm text-muted-foreground">
                      <T context="compose reply no sending mailbox available">
                        No sending mailbox available — ask an admin for access.
                      </T>
                    </span>
                  ) : (
                    <Select value={fromUpn ?? ''} onValueChange={setFromUpn}>
                      <SelectTrigger className="h-8 flex-1 border-0 bg-transparent px-0 shadow-none focus:ring-0">
                        <SelectValue
                          placeholder={gt('Select a sending mailbox', {
                            context: 'compose reply from placeholder',
                          })}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {(sendable?.mailboxes ?? []).map((mailbox) => (
                          <SelectItem key={mailbox.upn} value={mailbox.upn}>
                            {mailbox.name?.trim()
                              ? `${mailbox.name} <${mailbox.upn}>`
                              : mailbox.upn}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <ComposeRecipientField
                  label={gt('To', { context: 'email field label To' })}
                  contacts={contacts}
                  recipients={to}
                  onChange={setTo}
                />
                <ComposeRecipientField
                  label={gt('Cc', { context: 'email field label Cc' })}
                  contacts={contacts}
                  recipients={cc}
                  onChange={setCc}
                />
                <ComposeRecipientField
                  label={gt('Bcc', { context: 'email field label Bcc' })}
                  contacts={contacts}
                  recipients={bcc}
                  onChange={setBcc}
                />

                {/* Subject */}
                <div className="px-3 py-1.5">
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={gt('Subject', {
                      context: 'placeholder for email subject line',
                    })}
                    className="h-8 border-0 bg-transparent px-0 font-medium shadow-none focus-visible:ring-0"
                  />
                </div>
              </div>

              {/* Body — formatting toolbar always shown under the text */}
              <div className="px-3 py-2">
                <HtmlWYSIWYGEditor
                  key={editorKey}
                  initialHtml={bodyHtml}
                  onChange={setBodyHtml}
                  showToolbar
                  toolbarPosition="bottom"
                  height={200}
                  placeholder={gt('Write your message...', {
                    context: 'placeholder for email body',
                  })}
                />
              </div>

              <ComposeAttachmentChips
                files={files}
                refs={refs}
                budget={budget}
                isPreviewable={isXlsxAttachment}
                onPreview={setPreviewAttachment}
                onRemoveFile={(index) =>
                  setFiles((prev) => removeFileAt(prev, index))
                }
                onRemoveRef={(documentId) =>
                  setRefs((prev) => removeRef(prev, documentId))
                }
              />

              {/* Suggested responses */}
              {showSuggestedResponses && (
                <div className="border-t px-3 py-2">
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                    <T context="compose reply suggested responses heading">
                      Suggested responses
                    </T>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED_RESPONSES.map((response) => (
                      <Button
                        key={response.key}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertSuggestedResponse(response.html)}
                      >
                        {response.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action row */}
            <div className="flex shrink-0 items-center gap-1 border-t bg-muted/30 px-3 py-2">
              <Button
                type="button"
                onClick={handleSend}
                disabled={
                  to.length === 0 ||
                  !fromUpn ||
                  sendReply.isPending ||
                  budget.isOver
                }
                className="gap-1.5"
              >
                <PaperPlaneRightIcon className="h-4 w-4" />
                {sendReply.isPending ? (
                  <T context="button, sending email in progress">Sending…</T>
                ) : (
                  <T context="button, send email">Send</T>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
      {previewAttachment && (
        <AttachmentPreviewDialog
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );

  return layer === 'above-dialog' ? createPortal(panel, document.body) : panel;
}
